/**
 * « Nouvel avoir fournisseur » — /avoirs/nouveau (design/ux/03_avoirs_bc_reclamations.md §1; refonte
 * WP-29). Every sentence of the editor, verbatim from the design where it gives one (§0.6, §1.4-§1.14),
 * once. Vouvoiement, sentence case, no raw enum value, no English. Refonte WP-82 (CP §3.2, §8, §8.1,
 * §8.2, §11): the type meanings, the optional factures of a crédit fournisseur, the two allocation
 * methods, the suggestion block and the « en instance » / « confirmé par le fournisseur » choice.
 * Refonte WP-86 (CP §5.1-§5.3, §11; D-33, A-85, A-111): the invoice lines with what remains to credit,
 * the line entry (motif, sous-type, quantity, lot), the stock choice of a « Toujours demander » motif
 * and the stock effect of each line; the excess refusal is the server's sentence (MESSAGES_AVOIR).
 * Refonte WP-87 (CP §5.4, §11, Scenario F1-F2 / G1; D-34, A-114): the « Reçu non facturé » entry —
 * Commandé / Non commandé, « Entrer en stock maintenant » / « À l'arrivée de la facture », lot,
 * péremption, magasin, emplacement, the suggested PU HT and PPA — the consequence on the stock and on
 * the solde said before the line is added; its refusals are the server's sentences (parity-tested).
 */
import {
  AVOIR_BORNES,
  DECISION_STOCK_LIGNE_AVOIR,
  LIBELLES_MODE_REPARTITION_AVOIR,
  RECU_NON_FACTURE_COMMANDE,
  RECU_NON_FACTURE_SAISIE,
  STATUT_COMPTABLE_AVOIR,
  TYPE_AVOIR,
} from "@pharmakon/shared/constants/avoirs.js";

export const PAGE_AV = Object.freeze({
  titre: "Nouvel avoir fournisseur",
  numeroAttribue: "N° attribué à l'enregistrement",
  enTete: "En-tête de l'avoir",
  enTeteBouton: "En-tête",
  allerLignes: "Aller aux lignes de l'avoir",
  interdit: "Vous n'avez pas les droits pour créer un avoir. Contactez le propriétaire de l'officine.",
  abandonTitre: "Abandonner la saisie ?",
  abandonTexte: "Les lignes saisies seront perdues.",
  abandonContinuer: "Continuer la saisie",
  abandonConfirmer: "Abandonner",
});

/** §1.4 — the radio group (help texts verbatim). */
export const TYPE_AV = Object.freeze({
  label: "Type d'avoir",
  // refonte WP-82 (CP §8.3, D-38) — one line of meaning per type, the words of the documents (D-10)
  aide: Object.freeze({
    [TYPE_AVOIR.ECART]: "Réduit le solde dû au fournisseur (remise, écart de facturation), avec ou sans facture. Aucun mouvement de stock.",
    // refonte WP-86 (D-33) — any line of a validated facture, one or several motifs per line
    [TYPE_AVOIR.PRODUITS]:
      "Produits manquants, abîmés, périmés ou retournés, crédités ligne par ligne sur les factures du fournisseur — plusieurs motifs possibles par ligne. Une marchandise reçue sans facture se saisit en « Reçu non facturé ».",
    [TYPE_AVOIR.FINANCIER]: "Ristourne ou remboursement : une recette à l'encaissement, sans effet sur le solde fournisseur.",
  }),
  changerTitre: "Changer le type d'avoir ?",
  changerTexte: (type) => `Les lignes et montants saisis seront recalculés pour le type « ${type} ».`,
  garder: "Garder le type actuel",
  changer: "Changer",
});

/** §1.5 and the header fields. */
export const EN_TETE_AV = Object.freeze({
  fournisseur: "Fournisseur",
  solde: "Solde fournisseur",
  date: "Date de l'avoir",
  reference: "Réf. avoir fournisseur",
  referenceAide: "Le numéro de l'avoir émis par le fournisseur, s'il vous l'a déjà remis.",
  motif: "Motif / observations",
  motifCourt: `Saisissez un motif d'au moins ${AVOIR_BORNES.motif_min} caractères.`,
  dateInvalide: "Saisissez la date de l'avoir (JJ/MM/AAAA).",
  autreFournisseurTitre: "Changer de fournisseur ?",
  autreFournisseurTexte: (autre) =>
    `Cette facture appartient à ${autre}. Un avoir ne concerne qu'un seul fournisseur. Changer de fournisseur ? La sélection actuelle sera vidée.`,
  changementFournisseurTexte:
    "Un avoir ne concerne qu'un seul fournisseur. Changer de fournisseur ? La sélection actuelle sera vidée.",
  garderFournisseur: (nom) => `Garder ${nom}`,
  changerFournisseur: "Changer de fournisseur",
  bande: (fournisseur, type, date, solde) => [fournisseur, type, date, solde].filter(Boolean).join(" · "),
});

/** §1.6 — factures d'origine. */
export const FACTURES_AV = Object.freeze({
  // WP-86 (CP §3.1-13, §5.1) — every VALIDÉE facture is listed; the quantity écarts only lead
  suggestionsProduits: "Factures récentes — écarts de quantité d'abord",
  // WP-82 (CP §8.1) — every VALIDÉE facture is listed, the écarts above the tolerance first
  suggestionsEcart: (tolerance) => `Factures récentes (facultatif) — écarts de total supérieurs à ${tolerance} DA d'abord`,
  suggestionsEcartSansTolerance: "Factures suggérées — écart de total",
  recherchesRistourne: "Factures — recherche",
  origine: (fournisseur, n) => `Factures d'origine — ${fournisseur}${n ? ` · ${n} cochée${n > 1 ? "s" : ""}` : ""}`,
  concernees: "Factures concernées (facultatif)",
  // WP-82 (CP §8, §8.1) — the factures of a crédit fournisseur are optional references or allocations
  associees: (fournisseur, n) => `Factures associées (facultatif) — ${fournisseur}${n ? ` · ${n} cochée${n > 1 ? "s" : ""}` : ""}`,
  resume: (liste) => `Factures d'origine · ${liste}`,
  modifier: "Modifier",
  rechercher: "Rechercher : N° facture, N° interne, fournisseur, montant",
  rechercherFournisseur: "Rechercher dans les factures de ce fournisseur",
  ecartsDabord: "Écarts d'abord",
  autresQuantite: "Autres factures (sans écart de quantité)",
  autresMontant: "Autres factures (sans écart de montant)",
  plus: (n) => `Afficher ${n} de plus`,
  charger: "Charger",
  aideSuggestions: "Entrée : choisir cette facture — son fournisseur est sélectionné et toutes ses factures s'affichent, écarts d'abord.",
  aideListe: "Espace : cocher · Entrée : charger les lignes des factures cochées",
  aideListeEcart: "Espace : cocher une facture (facultatif) · Entrée : aller au montant",
  allerMontant: "Aller au montant",
  colonnes: Object.freeze({
    numero: "N° facture",
    interne: "N° int.",
    date: "Date",
    fournisseur: "Fournisseur",
    motifs: "Motifs",
    ecartQte: "Écart qté",
    ecartTtc: "Écart TTC",
    dejaCredite: "Déjà crédité",
    aCrediter: "À créditer",
  }),
  // WP-82 — « Montant global »: a facture named without a part
  reference: "référence",
  grille: "Factures d'origine",
  motifQt: "Écart de quantité : au moins une ligne où Qté payée ≠ Qté reçue (information : toutes les factures validées restent sélectionnables)",
  motifMoney: (tolerance) => `Écart de montant : total TTC déclaré − total TTC calculé > ${tolerance} DA`,
  ecartQte: (lignes, unites) => `${lignes} l. · ${unites} u.`,
  aVerifier: (montant) => `${montant} · à vérifier`,
  // refonte WP-87 (CP §5.4) — goods received without an invoice line need no facture
  videProduits:
    "Aucune facture validée pour ce fournisseur : un avoir produits se rattache aux lignes d'une facture validée — pour une marchandise reçue sans facture, saisissez un « Reçu non facturé » (Alt+N).",
  videEcart: "Aucune facture validée pour ce fournisseur. Le crédit fournisseur s'enregistre aussi sans facture : saisissez son montant.",
  videSuggestions: "Aucune facture récente ne présente d'écart. Choisissez un fournisseur pour voir toutes ses factures.",
  videRistourne: "Choisissez un fournisseur pour voir ses factures, ou recherchez une facture.",
  videRecherche: "Aucune facture ne correspond à cette recherche.",
  erreur: (message) => `Les factures de ce fournisseur n'ont pas pu être chargées. ${message ?? ""}`.trim(),
  reessayer: "Réessayer",
  aucuneCochee: "Cochez au moins une facture (Espace), puis chargez ses lignes (Entrée).",
  tropDeFactures: `Un avoir porte au plus ${AVOIR_BORNES.factures_max} factures.`,
});

/**
 * §1.7 as amended by CP §5.2-§5.3 (refonte WP-86) — « Lignes des factures cochées » (every invoice line,
 * what remains to credit) and « Lignes de l'avoir » (one or several per invoice line, each with its
 * motif and its stock decision).
 */
export const SOURCES_AV = Object.freeze({
  titre: "Lignes des factures cochées",
  grille: "Lignes des factures cochées",
  aide: "↑ ↓ : choisir une ligne · Entrée : créditer cette ligne (motif, quantité) — plusieurs motifs possibles sur une même ligne.",
  colonnes: Object.freeze({
    produit: "Produit",
    payee: "Payée",
    ug: "UG",
    recue: "Reçue",
    deja: "Déjà crédité",
    dansAvoir: "Dans cet avoir",
    restant: "Reste à créditer",
  }),
  ecartLivraison: (u) => `Écart de livraison : ${u} u.`,
  ecartAide: "Information : Qté payée − Qté reçue non encore créditée. Ce n'est ni une limite ni une condition — la limite est le reste à créditer.",
  restantAide: "Qté payée − toutes les lignes d'avoir non annulées de cette ligne, tous motifs confondus (les UG ne se créditent pas).",
  dansAvoir: (u, n) => `${u} u. · ${n} ligne${n > 1 ? "s" : ""}`,
  soldee: "Entièrement créditée",
  crediter: "Créditer cette ligne",
  autresLots: (n) => `+${n} lot${n > 1 ? "s" : ""}`,
  vide: "Les factures cochées n'ont aucune ligne de produit.",
  chargement: "Chargement des lignes des factures…",
});

export const LIGNES_AV = Object.freeze({
  titre: "Lignes de l'avoir",
  grille: "Lignes de l'avoir",
  ajouter: "Ajouter une ligne",
  colonnes: Object.freeze({
    motif: "Motif",
    produit: "Produit",
    qte: "Qté avoir",
    pu: "PU net HT",
    tva: "TVA",
    ttc: "Total TTC",
  }),
  puAide: "Prix unitaire HT net de ristourne et de remise globale, tel que facturé.",
  ligneSecondaire: (facture, lot) => [facture && `Fact. ${facture}`, lot && `Lot ${lot}`].filter(Boolean).join(" · "),
  // the stock effect of a line (the persisted decision — D-33, A-111)
  // the line already names its lot: the effect says the units only (the summary names the lot)
  // refonte WP-129 (D-50) — the bon de retour is asked after the save (« Créer un bon de retour pour ces produits ? »)
  sortieStock: (u) => `stock −${u} u. (retour au fournisseur)`,
  correctionStock: (u, ecart) =>
    [
      u > 0 ? `stock −${u} u. corrigée${u > 1 ? "s" : ""} (facturé non reçu)` : "stock inchangé",
      ecart > 0 ? `${ecart} u. jamais entrée${ecart > 1 ? "s" : ""} en stock` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  stockInchange: "stock inchangé",
  choixAttendu: "choix de stock à faire",
  choixFait: (choix) => `votre choix : ${choix}`,
  changerChoix: "Changer le choix de stock",
  // WP-31 (OP-03-21) — a line crediting a validated bon de retour
  dejaSorti: (numero) => `stock déjà sorti — bon de retour ${numero}`,
  // refonte WP-89 (D-33, D-36) — a line whose réclamation line took its units out « maintenant » (no second exit);
  // the bound is the server's sentence (MESSAGES_AVOIR.DEJA_DEDUIT_AU_DELA)
  dejaDeduitReclamation: (numero) => `Stock déjà déduit par ${numero}`,
  dejaDeduitAuDela: (numero, reste) =>
    `La réclamation ${numero} n'a plus que ${reste} unité${reste > 1 ? "s" : ""} déduite${reste > 1 ? "s" : ""} du stock à reprendre sur cette ligne : saisissez une ligne de ${reste} u. liée à la réclamation et, pour le reste, une ligne distincte.`,
  bonAuDela: (numero, reste) => `Impossible de créditer plus que les unités du bon de retour ${numero} restant à créditer (${reste} u.).`,
  /**
   * The server's sentence (MESSAGES_AVOIR.LIGNE_AU_DELA_ELIGIBLE, 409 AVOIR_QUANTITE_DEPASSEE — parity
   * tested), then what the other lines of this avoir already take on the same invoice line.
   */
  auDelaEligible: (produit, eligible, autres = 0) =>
    `Quantité trop élevée pour « ${produit} » : il reste ${eligible} unité${eligible > 1 ? "s" : ""} payée${eligible > 1 ? "s" : ""} à créditer sur cette ligne de facture (les avoirs non annulés déjà saisis, tous motifs confondus, sont déduits ; les UG ne se créditent pas).${
      autres > 0 ? ` Les autres lignes de cet avoir en créditent déjà ${autres} u.` : ""
    }`,
  quantiteMin: "Saisissez une quantité d'au moins 1, ou retirez la ligne (Suppr).",
  stockInsuffisant: (lot, dispo) => `Stock disponible insuffisant sur le lot ${lot} (${dispo} u.).`,
  retourAuDela: (max) => `Vous ne pouvez pas retourner plus que la quantité reçue sur cette facture (${max} u.).`,
  choixRequis: (motif, produit) => `Le motif « ${motif} » demande votre choix pour « ${produit} » : « Déduire du stock » ou « Ne pas déduire du stock ».`,
  motifIndisponible: (motif) =>
    `Le motif « ${motif} » n'est plus actif (Paramètres › Achats › Motifs des réclamations et avoirs) : retirez la ligne et choisissez un autre motif.`,
  vide: "Aucune ligne pour l'instant : choisissez une ligne de facture ci-dessus et appuyez sur Entrée, ajoutez une ligne (F3) ou un reçu non facturé (Alt+N).",
  videAvantChargement:
    "Aucune ligne pour l'instant : cochez les factures d'origine puis chargez leurs lignes (Entrée), ou saisissez un reçu non facturé (Alt+N).",
  // refonte WP-87 (CP §5.4, D-34) — a « Reçu non facturé » line in « Lignes de l'avoir »
  rnfSecondaire: (reponse, lot) => ["Sans facture", reponse, lot && `Lot ${lot}`].filter(Boolean).join(" · "),
  rnfStock: Object.freeze({
    [DECISION_STOCK_LIGNE_AVOIR.ENTREE_IMMEDIATE]: (u, magasin) => `stock +${u} u. à l'enregistrement${magasin ? ` (${magasin})` : ""}`,
    [DECISION_STOCK_LIGNE_AVOIR.ENTREE_DIFFEREE]: () => "entrée en stock à l'arrivée de la facture",
    [DECISION_STOCK_LIGNE_AVOIR.AUCUN_MOUVEMENT]: () => "hors stock — en attente d'enlèvement",
  }),
  horsCredit: "Hors crédit",
  horsCreditAide: (valeur) =>
    `Ligne « Reçu non facturé » : elle ne crédite rien et ne réduit pas le solde du fournisseur. Valeur indicative : ${valeur} (Qté × PU HT, TVA du produit).`,
  puRnfAide: "PU HT gardé à titre indicatif : la facture à venir (commandé) ou l'enlèvement (non commandé) fixera la valeur.",
  modifierRnf: "Modifier la ligne",
  rnfIndisponible:
    "Le motif « Reçu non facturé » est désactivé (Paramètres › Achats › Motifs des réclamations et avoirs) : réactivez-le, ou retirez la ligne.",
  retirer: "Retirer la ligne",
  tropDeLignes: `Un avoir porte au plus ${AVOIR_BORNES.lignes_max} lignes.`,
  chargement: "Chargement des lignes des factures…",
  erreurChargement: (message) => `Les lignes des factures n'ont pas pu être chargées. ${message ?? ""}`.trim(),
  conflit:
    "Une autre saisie a crédité une partie de ces lignes de facture entre-temps : les restes à créditer ont été relus. Corrigez les lignes signalées avant d'enregistrer.",
  reglesModifiees:
    "La règle de stock d'un motif a changé entre-temps (Paramètres › Achats › Motifs des réclamations et avoirs) : les lignes signalées ont été mises à jour, vérifiez-les avant d'enregistrer.",
  motifsErreur: (message) => `Les motifs d'avoir n'ont pas pu être chargés. ${message ?? ""}`.trim(),
});

/** §1.7 as amended by CP §5.2 (refonte WP-86) — the line entry row: invoice line, motif, précision, Qté, lot. */
export const ENTREE_AV = Object.freeze({
  titre: "Nouvelle ligne d'avoir",
  ligne: "Ligne de facture",
  ligneAide: "Lignes des factures cochées : produit, facture, reste à créditer",
  ligneOption: (facture, lot, payee, restant) => [facture && `Fact. ${facture}`, lot && `Lot ${lot}`, `payée ${payee}`, `reste ${restant}`].filter(Boolean).join(" · "),
  horsFactures: "Ce produit ne figure pas sur les factures cochées : cochez sa facture d'abord.",
  motif: "Motif",
  motifAide: "Motifs d'avoir produits actifs (Paramètres › Achats › Motifs des réclamations et avoirs)",
  aucunMotif: "Aucun motif d'avoir produits actif : activez-en un dans Paramètres › Achats › Motifs des réclamations et avoirs.",
  sousType: "Précision",
  qte: "Qté",
  qteAide: (restant) => `reste ${restant} u. à créditer`,
  lot: "Lot",
  lotOption: (numero, peremption, dispo) => [numero ?? "Sans n° de lot", peremption, `dispo ${dispo}`].filter(Boolean).join(" · "),
  motifRetour: "Motif du bon de retour",
  ajouter: "Ajouter — Entrée",
  annuler: "Annuler — Échap",
  // the stock rule of the motif chosen, before anything is added (CP §11: the consequence first)
  effet: Object.freeze({
    RETOUR: "Stock : déduit — les unités sortent du lot à l'enregistrement ; le bon de retour pour le fournisseur vous est proposé ensuite.",
    CORRECTION: "Stock : déduit — les unités facturées non reçues, entrées à la validation de la facture, sont retirées du lot à l'enregistrement.",
    AUCUN: "Stock : inchangé.",
    DEMANDER: "Stock : ce motif demande votre choix — « Déduire du stock » ou « Ne pas déduire du stock » — avant d'ajouter la ligne.",
  }),
  ecart: (u) => `Écart de livraison constaté : ${u} u. (information)`,
  ligneRequise: "Choisissez la ligne de facture à créditer.",
  ligneSoldee: (produit, eligible) =>
    `La ligne « ${produit} » est entièrement créditée : ${eligible === 0 ? "toutes ses unités payées le sont déjà" : "le reste est pris par les autres lignes de cet avoir"}.`,
  motifRequis: "Choisissez le motif de la ligne.",
  sousTypeRequis: (motif) => `Le motif « ${motif} » demande une précision : choisissez-la.`,
  sansLot:
    "Cette ligne de facture n'a plus de lot en stock : ses unités ne peuvent pas être retournées. Choisissez « Ne pas déduire du stock » ou un autre motif.",
  lotRequis: "Choisissez le lot d'où sortent les unités.",
  motifRetourRequis: "Choisissez le motif du bon de retour : Cassé, Périmé ou Erreur de livraison.",
  doublon: (motif) => `Cette ligne de facture porte déjà une ligne « ${motif} » sur ce lot : modifiez sa quantité plutôt que d'en ajouter une seconde.`,
});

/**
 * CP §3.2, §4.2, §5.3, §11 (refonte WP-86) — the stock choice of a « Toujours demander » motif: the line
 * named (product, facture, lot, quantity), each answer with when and how the stock changes.
 */
export const CHOIX_STOCK_AV = Object.freeze({
  titre: "Effet sur le stock",
  intro: (motif) => `Le motif « ${motif} » demande votre choix pour cette ligne.`,
  label: "Stock de cette ligne",
  produit: "Produit",
  facture: "Facture",
  lot: "Lot",
  quantite: "Quantité",
  deduire: Object.freeze({
    RETOUR: (u, lot) => `${u} u. sortent du lot ${lot ?? "—"} à l'enregistrement de l'avoir ; le bon de retour pour le fournisseur vous est proposé ensuite.`,
    CORRECTION: (u, lot) =>
      `Les unités facturées non reçues, entrées en stock à la validation de la facture, sont retirées du lot ${lot ?? "—"} à l'enregistrement de l'avoir (au plus ${u} u.).`,
  }),
  nePasDeduire: (u, lot) => `Le stock ne change pas : les ${u} u. restent sur le lot ${lot ?? "—"}.`,
  requis: "Choisissez « Déduire du stock » ou « Ne pas déduire du stock ».",
  valider: "Valider le choix",
  revenir: "Revenir à la ligne",
});

const S_ = (n) => (n > 1 ? "s" : "");
const C_ = RECU_NON_FACTURE_COMMANDE;
const D_ = DECISION_STOCK_LIGNE_AVOIR;

/**
 * Refonte WP-87 (CP §5.4, §11, Scenario F1-F2 / G1; D-34, A-114) — the « Reçu non facturé » entry:
 * goods received without an invoice line. The two answers are asked, never assumed (nothing is
 * preselected — the server requires them); the consequence on the stock and on the solde is said
 * before the line is added. The refusals marked « serveur » are MESSAGES_AVOIR's own sentences
 * (apps/server/src/config/avoir.config.js — parity-tested), so the editor and the server say the same.
 */
export const RNF_AV = Object.freeze({
  titre: "Reçu non facturé",
  groupe: "Nouvelle ligne « Reçu non facturé »",
  intro: "Marchandise reçue sans ligne de facture : le produit, la quantité reçue, et si elle était commandée.",
  acces: "Marchandise reçue sans facture ?",
  bouton: "Reçu non facturé",
  produit: "Produit reçu",
  produitPlaceholder: "Nom, DCI, code-barres…",
  qte: "Qté reçue",
  commande: "Marchandise",
  commandeOptions: Object.freeze({
    [C_.COMMANDE]: Object.freeze({ label: "Commandé", aide: "Vous l'aviez commandée : une facture du fournisseur est attendue." }),
    [C_.NON_COMMANDE]: Object.freeze({
      label: "Non commandé",
      aide: "Vous ne l'aviez pas commandée : le fournisseur viendra la reprendre (aucune facture attendue).",
    }),
  }),
  entree: "Entrée en stock",
  entreeOptions: Object.freeze({
    [D_.ENTREE_IMMEDIATE]: Object.freeze({
      label: "Entrer en stock maintenant",
      aide: "Vendable dès l'enregistrement de l'avoir ; la facture à venir ne l'entrera pas une seconde fois.",
    }),
    [D_.ENTREE_DIFFEREE]: Object.freeze({
      label: "À l'arrivée de la facture",
      aide: "Rien n'est vendable avant la facture : les unités entreront en stock une seule fois, à sa validation.",
    }),
  }),
  lot: "N° de lot",
  peremption: "Péremption",
  magasin: "Magasin",
  magasinPrevu: "Magasin prévu (facultatif)",
  magasinPrevuVide: "Non précisé",
  principal: (nom) => `${nom} (principal)`,
  magasinsChargement: "Chargement des magasins…",
  magasinsErreur: "Les magasins n'ont pas pu être chargés.",
  reessayer: "Réessayer",
  emplacement: "Emplacement",
  emplacementFacultatif: "Facultatif : l'emplacement du magasin où les unités sont rangées.",
  emplacementPlaceholder: "Code (AA-00)",
  emplacementVide: "Aucun emplacement de ce magasin ne correspond.",
  emplacementSansMagasin: "Choisissez d'abord le magasin.",
  puHt: "PU HT",
  puHtIndicatif: "PU HT (indicatif)",
  puAide: "Valorise le lot jusqu'à la facture ; modifiable.",
  puIndicatifAide: "Valeur indicative de la ligne (hors crédit) ; modifiable.",
  ppa: "PPA",
  // the consequence, before anything is added (CP §11 — the document, the product, the quantity)
  effet: Object.freeze({
    choisir: "Indiquez si la marchandise était commandée : la suite en dépend.",
    choisirEntree: "Commandé : choisissez quand les unités entrent en stock.",
    [D_.ENTREE_IMMEDIATE]: (u, lot, magasin) =>
      `Stock : +${u} u. entrent maintenant${lot ? ` dans le lot ${lot}` : ""}${magasin ? ` (${magasin})` : ""}, vendables dès l'enregistrement — la facture à venir ne les entrera pas une seconde fois.`,
    [D_.ENTREE_DIFFEREE]: (u) => `Stock : rien n'entre maintenant — les ${u} u. entreront une seule fois, à la validation de la facture liée.`,
    [D_.AUCUN_MOUVEMENT]: (u) => `Stock : aucune entrée — les ${u} u. restent « En attente d'enlèvement » par le fournisseur.`,
  }),
  soldeCommande: "Solde fournisseur : inchangé par cet avoir — c'est la facture à venir qui le modifiera.",
  soldeNonCommande:
    "Solde fournisseur : inchangé (marchandise jamais facturée). À l'enlèvement, le bon de retour vous demandera s'il doit avoir un effet sur le solde.",
  ajouter: "Ajouter — Entrée",
  mettreAJour: "Mettre à jour — Entrée",
  annuler: "Annuler — Échap",
  aide: "Entrée : ajouter la ligne · Échap : annuler · 1 / 2 : choisir une réponse",
  // client-side refusals (the server's sentences where it has one)
  produitRequis: "Choisissez le produit reçu.",
  quantiteMin: "Saisissez la quantité reçue (au moins 1).",
  // serveur — validators/avoir.validator.js (ligneSchema)
  quantiteMax: `Quantité : ${RECU_NON_FACTURE_SAISIE.quantite_max} unités au plus par ligne`,
  prixMax: `PU HT : ${RECU_NON_FACTURE_SAISIE.prix_ht_max} DA au plus`,
  ppaMax: `PPA : ${RECU_NON_FACTURE_SAISIE.ppa_max} DA au plus`,
  lotTropLong: `N° de lot : ${RECU_NON_FACTURE_SAISIE.numero_lot_max} caractères au plus.`,
  // serveur — MESSAGES_AVOIR.RNF_*
  commandeRequise:
    "« Reçu non facturé » : indiquez si la marchandise était « Commandé » (une facture est attendue) ou « Non commandé » (le fournisseur viendra la reprendre).",
  entreeRequise: (produit) =>
    `« Reçu non facturé » commandé${produit ? ` (« ${produit} »)` : ""} : choisissez « Entrer en stock maintenant » ou « Entrer en stock à l'arrivée de la facture ».`,
  lotRequis: "Entrée en stock maintenant : saisissez le N° de lot des unités reçues.",
  magasinRequis: "Entrée en stock maintenant : choisissez le magasin de réception.",
  peremptionRequise: (produit) =>
    `Date de péremption obligatoire pour « ${produit} » (médicament ou parapharmacie) : saisissez-la avant d'entrer les unités en stock.`,
  peremptionPassee: "La date de péremption doit être dans le futur : vérifiez la date imprimée sur la boîte.",
  prixRequis: (produit) =>
    `Entrée en stock maintenant : saisissez le PU HT de « ${produit} » (aucun prix d'achat connu à suggérer) — il valorise le lot jusqu'à la facture.`,
  ppaRequis: (produit) => `Entrée en stock maintenant : saisissez le PPA de « ${produit} » (le produit n'en a pas) — le lot doit avoir un prix de vente.`,
  venteAPerte: (produit) => `Le PPA de « ${produit} » doit être supérieur ou égal à son PU HT (interdiction de vendre à perte).`,
  enDouble:
    "Le même produit figure deux fois en « Reçu non facturé » avec la même réponse, la même entrée et le même lot : regroupez les quantités.",
  motifIndisponible:
    "Le motif « Reçu non facturé » est désactivé (Paramètres › Achats › Motifs des réclamations et avoirs) : réactivez-le pour saisir une marchandise reçue sans facture.",
});

/** Refonte WP-86 — the motif pre-filled for an avoir produits whose lines carry several motifs (§1.12). */
export const MOTIF_AV = Object.freeze({ produits: "Avoir produits" });

/** §1.8 / §1.9 — montants. */
export const MONTANT_AV = Object.freeze({
  titre: "Montant de l'avoir",
  totalTtc: "TOTAL TTC AVOIR",
  calcule: (somme) => `calculé : ${somme} (Σ des écarts)`,
  ventilation: (ht, tva) => `Ventilation : HT ${ht} · TVA ${tva} — écarts « déclaré − calculé » (D-09)`,
  ventilationIndisponible: "Ventilation HT / TVA indisponible : seul le TTC déclaré a été saisi.",
  depasse: (somme) => `Le montant dépasse l'écart constaté sur les factures cochées (${somme} DA). Vérifiez l'avoir du fournisseur.`,
  partiel: (reste) => `Avoir partiel : ${reste} DA d'écart resteront à créditer sur ces factures.`,
  excedent: (montant) => `Dont ${montant} DA non imputés sur une facture : crédit chez le fournisseur.`,
  // refonte WP-81 (CP §8.2, D-38) — « Montant global » over several factures: references, no split
  global: "Montant global : les factures cochées sont citées en référence, sans répartition du montant entre elles.",
  // refonte WP-82 (CP §8.2, A-81) — a part above the facture's net dû is kept; the rest is a credit at the fournisseur
  partAuDelaNet: (facture, net, reste) =>
    `Facture ${facture} : ${reste} DA au-delà de son net dû (${net} DA) resteront un crédit chez le fournisseur.`,
  // refonte WP-82 (CP §8.2) — the two allocation methods, mutually exclusive
  repartition: "Répartition du montant",
  methodes: Object.freeze({
    GLOBAL: Object.freeze({ label: LIBELLES_MODE_REPARTITION_AVOIR.GLOBAL, aide: "Un seul montant pour l'avoir ; les factures cochées sont citées en référence." }),
    PAR_FACTURE: Object.freeze({
      label: LIBELLES_MODE_REPARTITION_AVOIR.PAR_FACTURE,
      aide: "Un montant par facture cochée, dans la colonne « À créditer » ; le total est leur somme.",
    }),
  }),
  parFactureSansFacture: "Cochez au moins une facture pour répartir le montant entre les factures.",
  methodeRequise:
    "Plusieurs factures sont cochées : choisissez « Montant global » (les factures sont citées en référence) ou « Répartition par facture » (un montant par facture).",
  totalParFacture: "TOTAL TTC AVOIR (somme des montants par facture)",
  aideParFacture: "Placez-vous sur la ligne d'une facture cochée et tapez son montant (colonne « À créditer »), puis Entrée.",
  ventilationSansFacture: (ht) => `Ventilation : HT ${ht} · TVA 0,00 — sans facture associée, aucun taux de TVA n'est appliqué.`,
  aucunStock: "Aucun mouvement de stock.",
  ristourne: "Montant de la ristourne (TTC)",
  ristourneInfo:
    "La ristourne sera comptabilisée en recette à son encaissement (Journal des avoirs → Enregistrer l'encaissement).",
  montantRequis: "Saisissez un montant strictement positif.",
});

/**
 * Refonte WP-82 (CP §8.1-§8.2) — the suggestion block of a crédit fournisseur: the server's positive
 * écarts (never a negative prefill, never a cap), each facture's state, the credits already citing it.
 */
export const SUGGESTION_AV = Object.freeze({
  titre: "Montant suggéré",
  sansFacture: "Sans facture associée : saisissez le montant accordé par le fournisseur. Le crédit réduit le solde du fournisseur sans être imputé sur une facture.",
  chargement: "Calcul du montant suggéré…",
  erreur: "Le montant suggéré n'a pas pu être calculé : saisissez le montant accordé par le fournisseur.",
  reessayer: "Réessayer",
  positif: (facture, ecart, deja, contribution) =>
    deja ? `${facture} : écart ${ecart} DA, déjà crédité ${deja} DA → ${contribution} DA` : `${facture} : écart ${ecart} DA`,
  dejaCredite: (facture, ecart) => `${facture} : écart ${ecart} DA, déjà entièrement crédité`,
  dansTolerance: (facture, ecart, tolerance) => `${facture} : écart ${ecart} DA, dans la tolérance (${tolerance} DA) — rien à suggérer`,
  negatif: (facture, ecart) => `${facture} : écart négatif (${ecart} DA), à vérifier — rien à suggérer`,
  aucun: (facture) => `${facture} : aucun écart de total`,
  citeePar: (numero, part, montant) => `déjà citée par l'avoir ${numero} (${part != null ? `part ${part} DA` : `montant global ${montant} DA`})`,
  total: (n, montant) => (n > 1 ? `Suggestion : ${montant} DA — somme des écarts positifs des ${n} factures` : `Suggestion : ${montant} DA — écart positif de la facture`),
  modifiable: "Le montant reste modifiable : une remise commerciale peut dépasser l'écart constaté.",
  utiliser: (montant) => `Reprendre la suggestion (${montant} DA)`,
});

/** §1.10 — totals bar. */
export const TOTAUX_AV = Object.freeze({
  dejaSorties: (u) => `${u} u. déjà sorties par un bon de retour`,
  /**
   * Refonte WP-86 — the lines and what they do to the stock (the persisted decisions, `totauxStock`):
   * « 3 lignes · −2 u. en stock (bon de retour) · 1 ligne sans mouvement de stock ».
   */
  // refonte WP-87 — and the « Reçu non facturé » lines: what enters now, later, what awaits collection
  composition: (n, { sorties = 0, corrigees = 0, dejaSorties = 0, dejaDeduites = 0, sansMouvement = 0, rnf = 0, entrees = 0, differees = 0, aEnlever = 0 } = {}) =>
    [
      `${n} ligne${n > 1 ? "s" : ""}`,
      sorties > 0 ? `−${sorties} u. en stock (retour au fournisseur)` : null,
      corrigees > 0 ? `−${corrigees} u. corrigée${corrigees > 1 ? "s" : ""} (facturé non reçu)` : null,
      dejaSorties > 0 ? `${dejaSorties} u. déjà sorties par un bon de retour` : null,
      dejaDeduites > 0 ? `${dejaDeduites} u. déjà déduites par la réclamation` : null,
      sansMouvement > 0 ? `${sansMouvement} ligne${sansMouvement > 1 ? "s" : ""} sans mouvement de stock` : null,
      rnf > 0 ? `${rnf} reçu${S_(rnf)} non facturé${S_(rnf)} (hors crédit)` : null,
      entrees > 0 ? `+${entrees} u. en stock maintenant` : null,
      differees > 0 ? `${differees} u. à l'arrivée de la facture` : null,
      aEnlever > 0 ? `${aEnlever} u. en attente d'enlèvement` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  htTva: (ht, tva) => `HT ${ht} · TVA ${tva}`,
  factures: (n) => `${n} facture${n > 1 ? "s" : ""}`,
  ristourne: "Ristourne",
  total: "TOTAL TTC AVOIR",
  // refonte WP-82 (D-32) — the solde moves only once the avoir is confirmed by the fournisseur
  soldeAvantApres: (avant, apres) => `Solde actuel : ${avant} · après confirmation : ${apres}`,
  sansFacture: "Sans facture associée",
  sansEffet: "Sans effet sur le solde fournisseur (recette à l'encaissement).",
  // refonte WP-87 (CP §5.4, A-114 (6)) — an avoir of « Reçu non facturé » lines only carries no credit
  sansEffetRnf: "Sans effet sur le solde fournisseur : un reçu non facturé ne porte aucun crédit.",
  soldeIndisponible: "Solde indisponible pour le moment.",
});

/** §1.11 — Enregistrer (F9), confirmation, success, errors. */
export const ENREGISTRER_AV = Object.freeze({
  action: "Enregistrer",
  enCours: "Enregistrement…",
  pret: "Enregistrer l'avoir",
  raisonType: "Choisissez le type d'avoir.",
  raisonFournisseur: "Choisissez le fournisseur.",
  raisonDate: "Saisissez une date d'avoir valide.",
  raisonProduits: "Choisissez au moins une facture et chargez ses lignes, ou saisissez un reçu non facturé (Alt+N).",
  // refonte WP-86 — the invoice lines are loaded, nothing is credited yet
  raisonLignes: "Ajoutez au moins une ligne à l'avoir : Entrée sur une ligne de facture, ou F3.",
  // refonte WP-82 (CP §8) — a crédit fournisseur needs no facture
  raisonEcart: "Saisissez le montant du crédit (TOTAL TTC AVOIR).",
  raisonParts: "Saisissez le montant d'au moins une facture (colonne « À créditer »).",
  raisonMethode: "Plusieurs factures sont cochées : choisissez « Montant global » ou « Répartition par facture ».",
  raisonSuggestion: "Calcul du montant suggéré en cours…",
  raisonRistourne: "Saisissez le montant de la ristourne (TTC).",
  raisonMotif: `Saisissez le motif (au moins ${AVOIR_BORNES.motif_min} caractères).`,
  raisonQuantite: "Corrigez les quantités signalées avant d'enregistrer.",
  confirmerTitre: "Enregistrer l'avoir ?",
  confirmer: "Enregistrer l'avoir",
  revenir: "Revenir à la saisie",
  resumeEntete: (type, fournisseur, factures) =>
    [`Avoir ${type}`, fournisseur, factures].filter(Boolean).join(" · "),
  facturesListe: (n, numeros) => `${n} facture${n > 1 ? "s" : ""} (${numeros})`,
  sansFacture: "sans facture associée",
  resumeMethode: (mode) => `Répartition : ${LIBELLES_MODE_REPARTITION_AVOIR[mode]}`,
  // refonte WP-86 — the lines by motif, the stock of each, the owner / manager's own choices
  resumeLignes: (n, parMotif) => `${n} ligne${n > 1 ? "s" : ""} : ${parMotif}`,
  motifUnites: (motif, u) => `${motif} ${u} u.`,
  resumeStock: (parts) => `Stock : ${parts}`,
  sortieLot: (u, lot) => `−${u} u. du lot ${lot}`,
  correctionLot: (u, lot) => `−${u} u. corrigée${u > 1 ? "s" : ""} sur le lot ${lot} (facturé non reçu)`,
  sansMouvement: (n) => `${n} ligne${n > 1 ? "s" : ""} sans mouvement`,
  resumeChoix: (motif, produit, choix) => `Votre choix pour « ${produit} » (${motif}) : ${choix}`,
  // WP-31 (OP-03-21) — where the returned units leave the stock
  // refonte WP-129 (D-50) — the units leave once, with the avoir; the bon de retour is asked right after the save
  resumeBonGenere: "Les unités des lignes déduites sortent du stock une seule fois, à l'enregistrement ; le bon de retour vous est proposé ensuite.",
  resumeTotal: (ttc) => `TOTAL TTC ${ttc}`,
  // refonte WP-89 (brief 2026-11-01 §2.4) — an avoir created from a réclamation
  resumeReclamation: (numero) => `Lié à la réclamation ${numero} : elle mentionnera cet avoir.`,
  dejaDeduitResume: (u, numero) => `${u} u. déjà déduite${u > 1 ? "s" : ""} par ${numero}`,
  // refonte WP-87 (CP §5.4, D-34, A-114) — the « Reçu non facturé » lines in the summary
  sansFactureRnf: "reçu sans facture",
  entreeRnf: (u, produit, lot, magasin) => `+${u} u. de « ${produit} » dans le lot ${lot ?? "—"}${magasin ? ` (${magasin})` : ""}`,
  differeeRnf: (u) => `${u} u. entreront à la validation de la facture`,
  enlevementRnf: (u) => `${u} u. en attente d'enlèvement (hors stock)`,
  resumeRnf: (n) =>
    `${n} ligne${S_(n)} « Reçu non facturé » hors crédit : ${n > 1 ? "elles ne réduisent" : "elle ne réduit"} pas le solde du fournisseur — la facture à venir (commandé) ou l'enlèvement (non commandé) ${n > 1 ? "les règlera" : "la règlera"}.`,
  resumeSansCycle: "Aucun crédit : l'avoir est enregistré tel quel, sans état « en instance ».",
  resumeRistourne: "Sans effet sur le solde fournisseur : la ristourne est une recette à son encaissement.",
  confirmerQuantites: (n) => `Je confirme les quantités modifiées (${n} ligne${n > 1 ? "s" : ""})`,
  confirmerDepassement: "Je confirme un montant supérieur à l'écart constaté.",
  confirmationRequise: "Cochez la confirmation pour enregistrer.",
  echec: (message) => `L'avoir n'a pas été enregistré : ${message}`,
  conflit:
    "Une autre saisie a déjà crédité une partie de ces quantités. Les écarts ont été recalculés : vérifiez les lignes avant d'enregistrer.",
  succes: (numero) => `Avoir ${numero} enregistré.`,
  // refonte WP-82 (D-32) — what the save did to the solde, by the lifecycle choice
  succesDetail: Object.freeze({
    [STATUT_COMPTABLE_AVOIR.INSTANCE]:
      "Enregistré en instance : le solde du fournisseur ne change pas tant que le fournisseur ne l'a pas confirmé ; le crédit figure dans les crédits en attente (non déduits).",
    [STATUT_COMPTABLE_AVOIR.CONFIRME]: "Confirmé par le fournisseur : le solde du fournisseur tient compte de l'avoir.",
    [TYPE_AVOIR.FINANCIER]: "La ristourne sera comptabilisée en recette à son encaissement.",
  }),
  // refonte WP-87 (A-114 (6)) — an avoir of « Reçu non facturé » lines only: no credit, nothing in the solde
  succesRnf:
    "Reçu non facturé enregistré : le solde du fournisseur ne change pas. Il se règle par la facture à venir (commandé) ou par l'enlèvement du fournisseur (non commandé).",
  succesRnfMixte: (n) => `Ses ${n} ligne${S_(n)} « Reçu non facturé » ${n > 1 ? "sont" : "est"} hors crédit : la facture à venir ou l'enlèvement ${n > 1 ? "les" : "la"} règlera.`,
  statut: "Statut",
  imprimer: "Imprimer",
  email: "Envoyer par e-mail",
  journal: "Voir dans le journal",
  nouvel: "Nouvel avoir",
  retourFacture: "Retour à la facture",
  impressionEchec: "Le document n'a pas pu être ouvert. Réessayez.",
  // refonte WP-82 (A-104 (2)) — the editor's creation key already used by another request
  conflitRequete: (numero) =>
    `Cette saisie a déjà enregistré l'avoir ${numero} avec d'autres valeurs : ouvrez-le pour voir ce qui a été enregistré, ou commencez un nouvel avoir.`,
  ouvrirAvoir: (numero) => `Ouvrir l'avoir ${numero}`,
});

/**
 * Refonte WP-82 (CP §3.2, §11, Scenario I2 / J; D-32, A-104) — the lifecycle choice of the
 * confirmation dialog, with the solde before → after of each option and the stock effect.
 */
export const CYCLE_AV = Object.freeze({
  label: "Statut de l'avoir",
  options: Object.freeze({
    [STATUT_COMPTABLE_AVOIR.INSTANCE]: Object.freeze({
      label: "Enregistrer en instance",
      aide: "Le fournisseur n'a pas encore émis ou accepté cet avoir : il attend sa confirmation.",
    }),
    [STATUT_COMPTABLE_AVOIR.CONFIRME]: Object.freeze({
      label: "Confirmé par le fournisseur",
      aide: "Le fournisseur a déjà émis ou accepté cet avoir.",
    }),
  }),
  effet: "Effet sur le solde fournisseur",
  soldeInchange: (solde) => `Solde fournisseur : ${solde} → inchangé`,
  attente: (avant, apres) => `Crédits en attente (non déduits) : ${avant} → ${apres}, jusqu'à la confirmation du fournisseur`,
  soldeAvantApres: (avant, apres) => `Solde fournisseur : ${avant} → ${apres}, dès l'enregistrement`,
  instanceSansSolde: (ttc) => `Le solde du fournisseur ne change pas : ${ttc} s'ajoutent aux crédits en attente jusqu'à la confirmation du fournisseur.`,
  confirmeSansSolde: (ttc) => `Le solde du fournisseur baisse de ${ttc} dès l'enregistrement.`,
  // refonte WP-83 — « Générer l'avoir » (the preview serves no pending-credit total: the amount added is said)
  attenteAjout: (ttc) => `${ttc} DA s'ajoutent aux crédits en attente (non déduits), jusqu'à la confirmation du fournisseur`,
  stockAucun: "Stock : aucun mouvement.",
  // refonte WP-86 — the persisted decision of each line (D-33): what leaves now, what is out already
  // refonte WP-87 — the « Reçu non facturé » entries now, at the invoice, or none (awaiting collection)
  stockProduits: ({ sorties = 0, corrigees = 0, dejaSorties = 0, dejaDeduites = 0, sansMouvement = 0, entrees = 0, differees = 0, aEnlever = 0 } = {}) =>
    `Stock : ${[
      sorties > 0 ? `−${sorties} u. sortent maintenant (retour au fournisseur)` : null,
      corrigees > 0 ? `−${corrigees} u. corrigée${corrigees > 1 ? "s" : ""} (facturé non reçu)` : null,
      dejaSorties > 0 ? `${dejaSorties} u. déjà sorties par un bon de retour` : null,
      dejaDeduites > 0 ? `${dejaDeduites} u. déjà déduites par la réclamation (aucune seconde sortie)` : null,
      sansMouvement > 0 ? `${sansMouvement} ligne${sansMouvement > 1 ? "s" : ""} sans mouvement de stock` : null,
      entrees > 0 ? `+${entrees} u. entrent maintenant (reçu non facturé)` : null,
      differees > 0 ? `${differees} u. entreront à la validation de la facture` : null,
      aEnlever > 0 ? `${aEnlever} u. en attente d'enlèvement (hors stock)` : null,
    ]
      .filter(Boolean)
      .join(" · ")}.`,
  stockIdentique: "Le stock est traité de la même façon, que l'avoir soit en instance ou confirmé.",
});

/** §1.1 — deep links. */
/**
 * Refonte WP-129 (D-50; brief 2026-10-26 §6) — after the save of an avoir produits whose lines were « Déduire du
 * stock »: « Créer un bon de retour pour ces produits ? » (Oui / Non); « Oui » creates it (no second exit, no second
 * credit) and offers to open it in a new window; « Non » keeps the button on the summary for later.
 */
export const BON_RETOUR_AV = Object.freeze({
  titre: "Créer un bon de retour pour ces produits ?",
  texte: (u, produits) =>
    `${u} u. (${produits}) sont sorties du stock avec l'avoir. Le bon de retour les documente pour le fournisseur : aucune nouvelle sortie de stock, aucun second crédit.`,
  oui: "Oui, créer le bon de retour",
  non: "Non",
  enCours: "Création du bon de retour…",
  cree: (numero) => `Bon de retour ${numero} créé.`,
  ouvrir: "Ouvrir le bon de retour",
  ouvrirAide: (numero) => `Ouvrir le bon de retour ${numero} dans une nouvelle fenêtre`,
  creer: "Créer le bon de retour",
  sansBon: (u) => `${u} u. sont sorties du stock avec l'avoir, sans bon de retour pour l'instant.`,
  echec: "Le bon de retour n'a pas pu être créé.",
});

/**
 * Refonte WP-89 (brief 2026-11-01 §2.4 « both cases … prefilled but editable … linked with the réclamation »;
 * K1 A5, DEF-K1-07) — the editor opened from a réclamation (`?reclamation=`): the band, the proposal of a
 * connected fournisseur, the proposed lifecycle and its reason, the consignation of a non-connected one.
 */
export const RECL_AV = Object.freeze({
  titre: (numero) => `Avoir lié à la réclamation ${numero}`,
  etat: Object.freeze({
    connecteAccepte: (date) => `fournisseur connecté : accepté le ${date}`,
    connecteAttente: "fournisseur connecté : en attente de sa réponse",
    nonConnecteConsigne: (date) => `fournisseur non connecté : réponse consignée le ${date}`,
    nonConnecteSansReponse: "fournisseur non connecté : pas encore de réponse",
  }),
  aide: "Toutes les valeurs reprises de la réclamation restent modifiables.",
  consulter: "Consulter la réclamation",
  consulterLabel: (numero) => `Consulter la réclamation ${numero} (nouvelle fenêtre)`,
  proposition: (montant) => `Le fournisseur propose un avoir${montant ? ` de ${montant} DA` : ""}, en attente de votre réponse.`,
  utiliserProposition: "Utiliser la proposition du fournisseur",
  propositionTitre: "Accepter la proposition du fournisseur ?",
  propositionTexte: "Vos modifications seront remplacées par les quantités et le montant de sa proposition.",
  propositionConfirmer: "Accepter la proposition",
  propositionAnnuler: "Annuler",
  propositionAcceptee: "Proposition acceptée : l'avoir reprend ses quantités et son montant.",
  raisonStatut: (raison) => `Proposé d'après la réclamation : ${raison}`,
  consigner: "Consigner la réclamation comme résolue",
  consignerAide: (numero) => `La réclamation ${numero} passe « Résolue » avec la note « Avoir AV-… créé ».`,
  chargement: "Lecture de la réclamation…",
  erreur: (message) => `La réclamation n'a pas pu être reprise : ${message}`,
  typeRequis: "Ce motif n'entraîne pas d'avoir par défaut : choisissez le type de l'avoir dans l'en-tête (F4).",
  succes: (avoir, reclamation) => `Avoir ${avoir} enregistré et lié à la réclamation ${reclamation}.`,
  retour: (numero) => `Retour à la réclamation ${numero}`,
  fournisseurVerrouille: "Le fournisseur est celui de la réclamation.",
});

export const LIENS_AV = Object.freeze({
  efactureNonImportee: "Cette e-facture n'a pas encore été importée en facture d'achat : importez-la d'abord.",
  factureIntrouvable: "La facture demandée est introuvable ou n'est pas validée : choisissez la facture dans la liste.",
  chargement: "Chargement de la facture…",
  // WP-31 — `?bon_retour=` (ux/03 §4.3 « Créer l'avoir produits »)
  chargementBon: "Chargement du bon de retour…",
  bonIntrouvable: "Le bon de retour demandé est introuvable dans votre officine.",
  bonNonValide: "Ce bon de retour n'est pas validé : validez-le avant d'en créer l'avoir.",
  bonCredite: (numero) => `Toutes les unités du bon de retour ${numero} sont déjà créditées par un avoir.`,
  bonCharge: (numero, n) => `Bon de retour ${numero} : ${n} ligne${n > 1 ? "s" : ""} « stock déjà sorti » à créditer.`,
});

/** The S3 « Facture d'achat » avoir actions (ux/02 §6.8). */
export const S3_AVOIRS = Object.freeze({
  creerFinancier: "Créer l'avoir financier",
  creerProduits: "Créer l'avoir produits",
  voirFinancier: (numero) => `Voir l'avoir financier ${numero}`,
  voirProduits: (numero) => `Voir l'avoir produits ${numero}`,
  voirFinanciers: "Voir les avoirs financiers",
  voirProduitsPlusieurs: "Voir les avoirs produits",
});

/** E-facture workspace « Avoir » tab (AUD-AVOIR-25). */
export const EFACTURE_AVOIRS = Object.freeze({
  intro: "Avoirs liés à la facture d'achat importée de cette e-facture (manquants, retours, écarts).",
  vide: "Aucun avoir lié pour le moment.",
  creer: "Créer un avoir",
  creerAria: "Créer un avoir lié à cette facture",
  erreur: "Les avoirs de cette e-facture n'ont pas pu être chargés.",
  reessayer: "Réessayer",
  colonnes: Object.freeze({ numero: "N° avoir", type: "Type", date: "Date", montant: "Montant TTC", etat: "État" }),
  grille: "Avoirs liés",
});
